/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import snakeCase from 'lodash/snakeCase';

import Imgix from 'js/components/Imgix';

import { Grid, Link, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { getCannedCollectionMetadata } from 'bundles/unified-seo-content-common/constants';

import _t from 'i18n!nls/articles';

type CollectionItem = {
  imageSrc: string;
  imageAlt: string;
  label: string;
  href: string;
};

const styles = {
  container: css`
    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-content: center;
    }
  `,
  collectionItemContainer: css`
    max-width: 330px;
    max-height: 260px;
    ${breakpoints.down('sm')} {
      margin-bottom: 10px;
    }
  `,
  header: css`
    margin-bottom: 24px;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  collectionItem: css`
    a,
    &:hover,
    &:focus {
      text-decoration: none;
    }

    p {
      color: #1f2022;
    }
  `,
  imageWrapper: css`
    height: 216px;
    width: 100%;
    overflow: hidden;
    margin-bottom: 10px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
};

const CannedCollectionItem = ({ imageSrc, imageAlt, label, href }: CollectionItem) => (
  <Link
    css={styles.collectionItem}
    component={TrackedLink2}
    variant="quiet"
    href={href}
    trackingName={`canned_collection_${snakeCase(label)}`}
  >
    <div>
      <div css={styles.imageWrapper}>
        <Imgix src={imageSrc} alt={imageAlt} maxWidth={330} maxHeight={216} loading="lazy" />
      </div>
      <Typography2 variant="subtitleMedium" component="p">
        {label}
      </Typography2>
    </div>
  </Link>
);

const CannedCollections = () => {
  return (
    <div>
      <div css={styles.header}>
        <Typography component="h3" variant="h2semibold">
          {_t('World-class learning for anyone, anywhere')}
        </Typography>
      </div>
      <Grid css={styles.container} container justifyContent="space-evenly" spacing={8}>
        {getCannedCollectionMetadata().map((cannedCollection) => (
          <Grid css={styles.collectionItemContainer} item xs={12} sm={6} md={4} key={snakeCase(cannedCollection.label)}>
            <CannedCollectionItem {...cannedCollection} />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default CannedCollections;
